import { LitElement, css, html } from "lit";

import { customElement, state } from "lit/decorators.js";

import { WowFormController } from "ui-common";
import { rpc } from "ui-common/services/rpc";
import "ui-components/button/wow-button.ts";
import "ui-components/form-elements/input/wow-input.ts";
import "ui-components/wow-link/wow-link";
import "ui-components/wow-logo/wow-logo";

import { isValidRedirectUrl } from "helpers/url";
import { t, translate } from "../translations/i18n";

const styles = css`
  :host {
    display: block;
    min-height: 100dvh;
    background-color: var(--sds-color-background-default-default);
    color: var(--sds-color-text-default-default);
    font-family: var(--sds-typography-family-body);
  }

  /* Base layout utilities */
  .container,
  .main-content,
  .header,
  .title-section,
  .form-section,
  .footer {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }

  .container {
    width: 100%;
    max-width: 393px;
    margin: 0 auto;
    padding: var(--sds-size-space-800);
    min-height: 100dvh;
  }

  .logo {
    margin-bottom: var(--sds-size-space-1600);
  }

  .main-content {
    gap: var(--sds-size-space-800);
  }

  .header,
  .title-section {
    gap: var(--sds-size-space-800);
  }

  .title-section {
    gap: var(--sds-size-space-400);
  }

  .title {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-06) / 1.2 var(--sds-typography-family-title);
  }

  .subtitle {
    font-family: var(--sds-typography-family-body);
    font-weight: var(--sds-typography-weight-regular);
    font-size: var(--sds-typography-scale-03);
    line-height: 1.4;
    color: var(--sds-color-text-default-secondary);
  }

  .back-link {
    font-family: var(--sds-typography-family-body);
    font-weight: var(--sds-typography-weight-regular);
    font-size: var(--sds-typography-scale-02);
    line-height: 1.4;
    text-align: center;
    text-decoration: none;
  }

  .back-link:hover {
    text-decoration: underline;
  }

  .form-section {
    gap: var(--sds-size-space-600);
  }

  .submit-button {
    display: grid;
  }

  .footer {
    gap: var(--sds-size-space-1200);
    margin-top: var(--sds-size-space-800);
  }

  .form-error {
    color: var(--sds-color-text-danger-default);
    font-size: var(--sds-typography-scale-02);
    margin-top: var(--sds-size-space-300);
    text-align: center;
  }

  .form-success {
    color: var(--sds-color-text-success-default);
    font-size: var(--sds-typography-scale-02);
    margin-top: var(--sds-size-space-300);
    text-align: center;
  }

  form {
    display: contents;
  }
`;

interface ForgotPasswordFormData {
  email: string;
}

@customElement("forgot-password-page")
export class ForgotPasswordPage extends LitElement {
  static override styles = [styles];

  @state()
  private formError = "";

  @state()
  private formSuccess = "";

  @state()
  private isSubmitting = false;

  private returnTo: string | undefined;

  override connectedCallback() {
    super.connectedCallback();
    const urlParams = new URLSearchParams(window.location.search);
    const returnTo = urlParams.get("returnTo");
    if (returnTo && isValidRedirectUrl(returnTo)) {
      this.returnTo = returnTo;
    }
  }

  private get signInHref(): string {
    if (this.returnTo) {
      return this.returnTo.replace(/^\/my\//, "");
    }
    return "sign-in";
  }

  private formController: WowFormController<ForgotPasswordFormData> = new WowFormController(
    this,
    {
      email: "",
    },
    {
      alwaysValidateOnInput: false,
      validateOnBlur: true,
      validators: {
        email: [
          (value: string) => {
            if (!value) {
              return translate("forgot-password.emailRequired");
            }
            if (!/\S+@\S+\.\S+/.test(value)) {
              return translate("forgot-password.invalidEmail");
            }
            return;
          },
        ],
      },
    },
    async (event) => {
      this.formError = "";
      this.formSuccess = "";

      if (!event || !event.data || !event.isValid) {
        return;
      }

      const { data, isValid } = event;

      if (!isValid) {
        this.formError = translate("forgot-password.fixErrors");
        return;
      }

      this.isSubmitting = true;

      try {
        const result = await rpc("request-password-reset", {
          email: data.email,
          ...(this.returnTo ? { returnTo: this.returnTo } : {}),
        });

        if (result.success) {
          this.formSuccess = result.message || translate("forgot-password.emailSent");
        } else {
          this.formError = result.message || translate("forgot-password.generalError");
        }
      } catch (error) {
        this.formError = translate("forgot-password.generalError");
        console.error("Password reset request error:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
  );

  protected override render() {
    return html`
      <div class="container">
        <div class="logo">
          <wow-logo></wow-logo>
        </div>

        <div class="main-content">
          <div class="header">
            <div class="title-section">
              <h1 class="title">${t("forgot-password.title")}</h1>
              <p class="subtitle">${t("forgot-password.subtitle")}</p>
            </div>
          </div>

          <form id="forgot-password-form">
            <div class="form-section">
              <wow-input
                name=${this.formController.fieldNames.email}
                type="email"
                placeholder=${t("forgot-password.email")}
                autocomplete="email"
                required
                ?disabled=${this.isSubmitting}
              >
              </wow-input>

              ${this.formError ? html`<div class="form-error">${this.formError}</div>` : ""}
              ${this.formSuccess ? html`<div class="form-success">${this.formSuccess}</div>` : ""}

              <wow-button
                form="forgot-password-form"
                class="submit-button"
                type="submit"
                ?disabled=${this.isSubmitting}
              >
                ${this.isSubmitting ? t("forgot-password.sending") : t("forgot-password.sendResetLink")}
              </wow-button>
            </div>
          </form>
        </div>

        <div class="footer">
          <p>
            ${t("forgot-password.backToSignIn")}
            <wow-link href=${this.signInHref}> ${t("forgot-password.signIn")} </wow-link>
          </p>
        </div>
      </div>
    `;
  }
}
